import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import './LandingIntroPage.css';

export const INTRO_SEEN_KEY = 'forza:intro:seen:v1';
export const INTRO_SOURCE = '/Forza_VideoIntro_NoWatermark.mp4';
export const INTRO_FALLBACK_TIMEOUT_MS = 35000;

export const buildLoginTarget = (search = '') => `/login${search || ''}`;

export const getAuthenticatedRedirectPath = (user) => {
  if (!user) return '/login';
  if (user.onboardingComplete === false) return '/onboarding';
  return '/dashboard';
};

export const shouldBypassIntro = ({ loading, user, seenIntro, reducedMotion }) => {
  if (loading) return false;
  if (user) return true;
  return Boolean(seenIntro || reducedMotion);
};

const LandingIntroPage = () => {
  const { user, loading } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const loginTarget = useMemo(() => buildLoginTarget(location.search), [location.search]);

  const clearFallbackTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const routeToLogin = useCallback(() => {
    clearFallbackTimeout();
    navigate(loginTarget, { replace: true });
  }, [clearFallbackTimeout, loginTarget, navigate]);

  const routeAuthenticatedUser = useCallback(() => {
    navigate(getAuthenticatedRedirectPath(user), { replace: true });
  }, [navigate, user]);

  const armFallbackTimeout = useCallback((durationSeconds) => {
    clearFallbackTimeout();
    const timeoutMs = Number.isFinite(durationSeconds) && durationSeconds > 0
      ? Math.ceil((durationSeconds + 2) * 1000)
      : INTRO_FALLBACK_TIMEOUT_MS;
    timeoutRef.current = window.setTimeout(() => {
      routeToLogin();
    }, timeoutMs);
  }, [clearFallbackTimeout, routeToLogin]);

  useEffect(() => {
    if (loading) return undefined;

    const seenIntro = sessionStorage.getItem(INTRO_SEEN_KEY) === 'true';
    const reducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

    if (user) {
      routeAuthenticatedUser();
      return undefined;
    }

    if (shouldBypassIntro({ loading, user, seenIntro, reducedMotion })) {
      routeToLogin();
      return undefined;
    }

    return undefined;
  }, [loading, routeAuthenticatedUser, routeToLogin, user]);

  useEffect(() => () => clearFallbackTimeout(), [clearFallbackTimeout]);

  const startPlayback = useCallback(async () => {
    if (loading || user || isPlaying || isStarting) {
      return;
    }

    setIsStarting(true);
    sessionStorage.setItem(INTRO_SEEN_KEY, 'true');

    const videoEl = videoRef.current;
    if (!videoEl) {
      routeToLogin();
      return;
    }

    try {
      videoEl.currentTime = 0;
      const maybePromise = videoEl.play();
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise;
      }
      setIsPlaying(true);
      armFallbackTimeout(videoEl.duration);
    } catch (error) {
      console.error('[LandingIntro] Video playback failed, redirecting to login.', error);
      routeToLogin();
    } finally {
      setIsStarting(false);
    }
  }, [armFallbackTimeout, isPlaying, isStarting, loading, routeToLogin, user]);

  const onKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      startPlayback();
    }
  }, [startPlayback]);

  return (
    <main
      className="landing-intro"
      onClick={startPlayback}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('landingIntro.startAria', 'Start Forza intro')}
    >
      <div className="landing-intro__background" aria-hidden="true" />

      <video
        ref={videoRef}
        className={`landing-intro__video ${isPlaying ? 'is-playing' : ''}`}
        src={INTRO_SOURCE}
        poster="/Forza_VideoIntro_Poster.jpg"
        preload="metadata"
        playsInline
        onLoadedMetadata={(event) => armFallbackTimeout(event.currentTarget.duration)}
        onPlaying={() => setIsStarting(false)}
        onEnded={routeToLogin}
        onError={routeToLogin}
      />

      {!isPlaying && (
        <div className="landing-intro__overlay">
          <div className="landing-intro__title-wrap">
            <p className="landing-intro__subprompt">{t('landingIntro.subprompt', 'A longevity app')}</p>
          </div>
          <div className="landing-intro__bottom-bar">
            <button
              type="button"
              className="landing-intro__cta"
              onClick={(event) => {
                event.stopPropagation();
                startPlayback();
              }}
            >
              {isStarting
                ? t('landingIntro.loading', 'Loading intro...')
                : t('landingIntro.enter', 'Tap to enter Forza')}
            </button>
            {isStarting && (
              <p className="landing-intro__loading">{t('landingIntro.preparing', 'Preparing video...')}</p>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default LandingIntroPage;
